import React, { useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { HeadlineMedium } from '@shared/ui/atoms/fonts/Headline';
import { BodyLarge, BodyRegular } from '@shared/ui/atoms/fonts/Body';
import { useTranslation } from "react-i18next";
import { ToggleableOutlineButton } from '@shared/ui/atoms/buttons/ToggleableOutlineButton';
import { PrimaryButton } from '@shared/ui/atoms/buttons/PrimaryButton';
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';
import { ContentCard } from '@shared/ui/atoms/ContentCard';
import Colours from '@shared/ui/atoms/Colours';
import { AccountType } from '@shared/models/company.models';
import { selectAccountInfo } from "@features/settings/store/payments.duck";
import { DialogIdentifiers, openDialogWithPayload } from '@shared/store/dialog.duck';
import { tablet } from '@shared/ui/atoms/MediaQueries';

// The upgradeable paid plans, ordered ascending by tier — excludes the free STANDARD plan and the
// FREE_TRIAL state. The ascending order is relied on to only show tiers above the current one.
const STAR_ICON = '/assets/icons/standard/Star.svg';

// `features` are the translation keys listed in each plan's feature card. `stars` controls how many
// star icons appear above the plan title (Professional 1, Premium 2, Enterprise 3).
const PLANS: { tier: AccountType; label: string; stars: number; features: string[] }[] = [
  {
    tier: AccountType.PROFESSIONAL,
    label: 'payments.professional-plan',
    stars: 1,
    features: [
      'payments.professional-custom-whereabouts',
      'payments.professional-meeting-rooms',
      'payments.advanced-reporting',
      'payments.priority-support',
      'payments.interactive-floor-plans',
      'payments.car-parking',
    ],
  },
  {
    tier: AccountType.PREMIUM,
    label: 'payments.premium-plan',
    stars: 2,
    features: [
      'payments.premium-features',
      'payments.interactive-floor-plans',
      'payments.car-parking',
      'payments.advanced-reporting',
      'payments.priority-support',
    ],
  },
  {
    tier: AccountType.ENTERPRISE,
    label: 'payments.enterprise-plan',
    stars: 3,
    features: [
      'payments.enterprise-features',
      'payments.premium-features',
      'payments.interactive-floor-plans',
      'payments.car-parking',
      'payments.advanced-reporting',
      'payments.priority-support',
    ],
  },
];

export function UpgradeSection() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const accountInfo = useSelector(selectAccountInfo);
  const [selectedTier, setSelectedTier] = useState<AccountType | null>(null);

  // Enterprise is the top tier — there's nothing to upgrade to, so hide the section.
  if (accountInfo?.accountType === AccountType.ENTERPRISE) {
    return null;
  }

  // Only offer tiers above the current one: Professional hides Professional; Premium hides
  // Professional and Premium. Unknown/free tiers (index -1) show all upgrade plans.
  const currentTierIndex = PLANS.findIndex(plan => plan.tier === accountInfo?.accountType);
  const upgradePlans = PLANS.filter((_, index) => index > currentTierIndex);

  // Default to the lowest offered upgrade; keep the user's choice while it remains a valid option.
  const activeTier = upgradePlans.some(plan => plan.tier === selectedTier) ? selectedTier : upgradePlans[0]?.tier;
  const selectedPlan = PLANS.find(plan => plan.tier === activeTier);

  // Never offer an upgrade into the plan the account is already on (e.g. the Premium card while
  // on Premium) — the tier filter above normally prevents this, but guard the button too.
  const isCurrentPlan = selectedPlan?.tier === accountInfo?.accountType;

  const upgradeClicked = () => {
    if (!selectedPlan) {
      return;
    }
    if (selectedPlan.tier === AccountType.ENTERPRISE) {
      // Enterprise is never self-service, even for Stripe customers — speak to an account manager.
      dispatch(openDialogWithPayload({
        activeDialog: DialogIdentifiers.UpgradeContactDialog,
        payload: { label: selectedPlan.label, stars: selectedPlan.stars, bodyKey: 'payments.enterprise-contact-body' },
      }));
    } else if (accountInfo?.usesStripe) {
      // Stripe customers upgrade self-service: confirm the change, then
      // ChangePlanConfirmDialog calls changeSubscriptionPlan(tier).
      dispatch(openDialogWithPayload({
        activeDialog: DialogIdentifiers.ChangePlanConfirmDialog,
        payload: { tier: selectedPlan.tier },
      }));
    } else {
      dispatch(openDialogWithPayload({
        activeDialog: DialogIdentifiers.UpgradeContactDialog,
        payload: { label: selectedPlan.label, stars: selectedPlan.stars },
      }));
    }
  };

  return (
    <Wrapper>
      <HeadlineMedium>{t('payments.looking-to-upgrade')}</HeadlineMedium>
      <PlanButtonRow>
        {upgradePlans.map(plan => (
          <ToggleableOutlineButton key={plan.tier}
                                   isActive={activeTier === plan.tier}
                                   size={ButtonSize.Large}
                                   click={() => setSelectedTier(plan.tier)}
                                   text={plan.label} />
        ))}
      </PlanButtonRow>

      {selectedPlan &&
        <FeatureCard>
          <FeatureColumns>
            <PlanTypeColumn>
              <StarRow>
                {Array.from({ length: selectedPlan.stars }).map((_, index) => (
                  <PlanIcon key={index} src={STAR_ICON} alt="" aria-hidden={true} />
                ))}
              </StarRow>
              <BodyLarge weight={600}>{t(selectedPlan.label)}</BodyLarge>
            </PlanTypeColumn>
            <FeatureColumn>
              <FeatureList>
                {selectedPlan.features.map(feature => (
                  <FeatureRow key={feature}>
                    <BodyRegular>{t(feature)}</BodyRegular>
                  </FeatureRow>
                ))}
              </FeatureList>
              {!isCurrentPlan &&
              <ContactButtonRow>
                <PrimaryButton size={ButtonSize.Large}
                               text={accountInfo?.usesStripe ? 'payments.upgrade-plan' : 'payments.contact-us'}
                               click={upgradeClicked} />
              </ContactButtonRow>}
            </FeatureColumn>
          </FeatureColumns>
        </FeatureCard>}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  margin-bottom: 32px;
  text-align: center;
`

const PlanButtonRow = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 16px;
  /* Single row on tablet and up; allowed to wrap on mobile. */
  @media (${tablet}) {
    flex-wrap: nowrap;
  }
`

const FeatureCard = styled(ContentCard)`
  margin-top: 24px;
  text-align: left;
`

const FeatureColumns = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: flex-start;
  /* Plan type sits to the left of the features on tablet and up; stacks on mobile. */
  @media (${tablet}) {
    flex-direction: row;
    gap: 32px;
    align-items: center;
  }
`

const PlanTypeColumn = styled.div`
  text-align: center;
  @media (${tablet}) {
    flex: 1;
  }
`

const StarRow = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
`

const PlanIcon = styled.img`
  display: block;
  width: 48px;
  height: 48px;
`

const FeatureColumn = styled.div`
  @media (${tablet}) {
    flex: 1;
  }
`

const FeatureList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`

const ContactButtonRow = styled.div`
  margin-top: 24px;
`

const FeatureRow = styled.li`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  &:last-child {
    margin-bottom: 0;
  }
  &::before {
    content: '';
    flex-shrink: 0;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: ${Colours.green};
  }
  p {
    margin: 0;
  }
`
